import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { FC, useMemo } from 'react';
import type { Key, HTMLAttributes } from 'react';
import { Autocomplete, InputAdornment } from '@mui/material';
import { CountryWithFlag } from 'ui/CountryWithFlag';
import { CountryFlag } from 'ui/CountryFlag';
import { getCountryListData } from 'redux/reducers/country/selectors';
import { useSelector } from 'react-redux';
// import countries from './countries-world.json';
import { TCountrySelectProps } from './types';
import { ECountryType, ETableStructureType } from '../EasTable/types';

export const CountrySelectMenu: FC<TCountrySelectProps> = ({
	onChange,
	onChangeNumber,
	onBlur,
	countryList,
	countryCode,
	countryNumber,
	label = 'Country',
	size = 'small',
	type = ETableStructureType.COUNTRY_CODE,
	typeOfCountry,
	error,
	helperText,
}) => {
	const countries = useSelector(getCountryListData);
	const allCountryList = useMemo(
		() => countries?.map((item) => ({ code: item.isoAlpha2, label: item.name, number: item.id })),
		[countries],
	);
	const euCountryList = useMemo(
		() =>
			countries
				?.filter((item) => item.isEu)
				.map((item) => ({ code: item.isoAlpha2, label: item.name, number: item.id })),
		[countries],
	);
	const nonEuCountryList = useMemo(
		() =>
			countries
				?.filter((item) => !item.isEu && item.isSupported)
				.map((item) => ({ code: item.isoAlpha2, label: item.name, number: item.id })),
		[countries],
	);
	const computedCountryList = useMemo(
		() =>
			countryList ??
			(typeOfCountry === ECountryType.EU
				? (euCountryList ?? [])
				: typeOfCountry === ECountryType.NON_EU
					? (nonEuCountryList ?? [])
					: (allCountryList ?? [])),
		[countryList, typeOfCountry, euCountryList, allCountryList, nonEuCountryList],
	);

	const country = computedCountryList.find((item) => item.code === countryCode) ?? {
		code: '',
		label: '',
		number: 0,
	};
	const countryNmbr = computedCountryList.find(
		(item) => (item?.number ?? 0) === (countryNumber ?? 0),
	) ?? {
		code: '',
		label: '',
		number: 0,
	};
	const selectedCountry = type === ETableStructureType.COUNTRY_CODE ? country : countryNmbr;

	return (
		<Box sx={{ width: '100%', m: '8px 0' }}>
			<Autocomplete
				sx={{
					width: '100%',
					'& .MuiInputBase-root': { width: '100%', borderRadius: '12px' },
				}}
				size={size}
				options={computedCountryList}
				onChange={(event, newValue) => {
					if (newValue) {
						if (type === ETableStructureType.COUNTRY_CODE) {
							onChange?.(newValue.code);
						} else {
							console.log('Autocomplete - newValue', newValue, newValue?.number ?? 0);
							onChangeNumber?.(newValue?.number ?? 0);
						}
					}
				}}
				onBlur={onBlur}
				value={selectedCountry}
				autoHighlight
				getOptionLabel={(option) => option.label}
				renderOption={(props, option) => {
					const { key, ...optionProps } = props as unknown as {
						key?: Key;
					} & HTMLAttributes<HTMLLIElement>;
					return (
						<Box component="li" key={key} {...optionProps}>
							<Box
								sx={{
									width: '100%',
									display: 'flex',
									// flexDirection: 'row',
									justifyContent: 'space-between',
									alignItems: 'center',

									'& > div img': { mr: 2, flexShrink: 0 },
								}}
							>
								<Box>
									<CountryWithFlag countryCode={option.code} />
								</Box>
								<Box>{option.code}</Box>
							</Box>
						</Box>
					);
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						placeholder="Choose a country"
						error={error}
						helperText={helperText}
						inputProps={{
							...params.inputProps,
							autoComplete: 'new-password',
						}}
						InputProps={{
							...params.InputProps,
							startAdornment: selectedCountry.code ? (
								<InputAdornment position="start">
									<CountryFlag countryCode={selectedCountry.code} size="xl" />
								</InputAdornment>
							) : null,
						}}
					/>
				)}
			/>
		</Box>
	);
};
